import React from 'react'
import classNames from 'classnames'
import { getExecutionStartedAt } from '../../../../models/CrossClient'
import { useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { selectCrossClientTeammates } from '../../../../store/crossClient/crossClientResults'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { CrossClientResultDetailSourceIcon } from '../Sources/CrossClientResultSourceIcon'
import { getCrossClientResultDetailSourceLabel } from '../Sources/utils'
import { executionClientQueryMode, isZocksSource } from '../../utils'
import { useCcqRun } from './useCcqRun'
import { CCQ_QUERY_DETAILS, CCQ_STARTED_BY_YOU, CCQ_ZOCKS, ccqClientsLabel, ccqStarted } from './ccqRunCopy'
import styles from './CcqRail.module.scss'

const DetailRow: React.FC<React.PropsWithChildren<{ label: string }>> = ({ label, children }) => (
  // `contents`, so each dt and dd sit directly in the list's grid.
  <div className={styles.detailRow}>
    <dt className='paragraph-small bold zocks-text-gray-900'>{label}</dt>
    <dd className='paragraph-small zocks-text-gray-900 mb-0 mw-0'>{children}</dd>
  </div>
)

export const CcqQueryDetails: React.FC = () => {
  const { criteria, execution, total } = useCcqRun()
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const teammates = useAppSelector(selectCrossClientTeammates)

  const zocksSource = execution.sources.find(isZocksSource)
  const railSources = [...(zocksSource ? [zocksSource] : []), ...execution.sources.filter((source) => !isZocksSource(source))]
  // Left out while the creator can't be named: teammates still loading, or one who has left the account.
  const startedBy = execution.createdBy.id === currentUser.id ? CCQ_STARTED_BY_YOU : getUserDisplayName(teammates[execution.createdBy.id])

  return (
    <section aria-labelledby='ccq-query-details-heading' className={classNames(styles.railEnter, 'd-flex flex-column gap-3 zocks-pl-2.0')}>
      <h2 id='ccq-query-details-heading' className='h5 mb-0'>
        {CCQ_QUERY_DETAILS}
      </h2>
      <dl className={classNames(styles.details, 'mb-0')}>
        <DetailRow label='Criteria'>
          <ul className='list-unstyled d-flex flex-column gap-2 mb-0'>
            {criteria.map((criterion) => (
              <li key={criterion} className='d-flex align-items-center gap-2'>
                <i className='bi bi-x-diamond-fill zocks-text-info' aria-hidden='true' />
                {criterion}
              </li>
            ))}
          </ul>
        </DetailRow>
        <DetailRow label='Sources'>
          <ul className='list-unstyled d-flex flex-wrap align-items-center gap-3 mb-0'>
            {railSources.map((source) => (
              <li key={getCrossClientResultDetailSourceLabel(source)} className='d-flex align-items-center gap-1'>
                <CrossClientResultDetailSourceIcon source={source} />
                {isZocksSource(source) ? CCQ_ZOCKS : getCrossClientResultDetailSourceLabel(source)}
              </li>
            ))}
          </ul>
        </DetailRow>
        <DetailRow label='Clients'>{ccqClientsLabel(executionClientQueryMode(execution), total)}</DetailRow>
        <DetailRow label='Started'>{ccqStarted(getExecutionStartedAt(execution), startedBy)}</DetailRow>
      </dl>
    </section>
  )
}
